/**
 * Currency Formatting Service
 * Centralized service for consistent currency formatting and localization
 */

export interface CurrencyConfig {
  code: string;
  symbol: string;
  locale: string;
  decimalPlaces: number;
  symbolPosition: 'before' | 'after';
}

export interface LocaleConfig {
  currency: CurrencyConfig;
  numberFormat: Intl.NumberFormatOptions;
}

class CurrencyFormattingService {
  private readonly supportedCurrencies: Record<string, CurrencyConfig> = {
    INR: {
      code: 'INR',
      symbol: '₹',
      locale: 'en-IN',
      decimalPlaces: 2,
      symbolPosition: 'before',
    },
    USD: {
      code: 'USD',
      symbol: '$',
      locale: 'en-US',
      decimalPlaces: 2,
      symbolPosition: 'before',
    },
    EUR: {
      code: 'EUR',
      symbol: '€',
      locale: 'en-EU',
      decimalPlaces: 2,
      symbolPosition: 'before',
    },
    GBP: {
      code: 'GBP',
      symbol: '£',
      locale: 'en-GB',
      decimalPlaces: 2,
      symbolPosition: 'before',
    },
  };

  private defaultCurrency: string = 'INR';

  /**
   * Set the default currency for the application
   */
  setDefaultCurrency(currencyCode: string): void {
    if (this.supportedCurrencies[currencyCode]) {
      this.defaultCurrency = currencyCode;
    } else {
      console.warn(`Currency ${currencyCode} is not supported. Using ${this.defaultCurrency} as default.`);
    }
  }

  /**
   * Get the default currency code
   */
  getDefaultCurrency(): string {
    return this.defaultCurrency;
  }

  /**
   * Get currency configuration
   */
  getCurrencyConfig(currencyCode?: string): CurrencyConfig {
    const code = currencyCode || this.defaultCurrency;
    return this.supportedCurrencies[code] || this.supportedCurrencies[this.defaultCurrency];
  }

  /**
   * Get all supported currencies
   */
  getSupportedCurrencies(): CurrencyConfig[] {
    return Object.values(this.supportedCurrencies);
  }

  /**
   * Format currency amount with proper localization
   */
  formatCurrency(
    amount: number,
    currencyCode?: string,
    options?: {
      showSymbol?: boolean;
      showCode?: boolean;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ): string {
    const config = this.getCurrencyConfig(currencyCode);
    const {
      showSymbol = true,
      showCode = false,
      minimumFractionDigits = config.decimalPlaces,
      maximumFractionDigits = config.decimalPlaces,
    } = options || {};

    // Format the number according to locale
    const formattedNumber = amount.toLocaleString(config.locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    });

    // Build the formatted string
    let result = formattedNumber;

    if (showSymbol) {
      if (config.symbolPosition === 'before') {
        result = `${config.symbol}${result}`;
      } else {
        result = `${result}${config.symbol}`;
      }
    }

    if (showCode) {
      result = `${result} ${config.code}`;
    }

    return result;
  }

  /**
   * Format currency amount from paise/cents to main currency unit
   * (e.g., convert 10000 paise to ₹100.00)
   */
  formatCurrencyFromMinorUnit(
    amountInMinorUnit: number,
    currencyCode?: string,
    options?: {
      showSymbol?: boolean;
      showCode?: boolean;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ): string {
    const config = this.getCurrencyConfig(currencyCode);
    const divisor = Math.pow(10, config.decimalPlaces);
    const amount = amountInMinorUnit / divisor;
    
    return this.formatCurrency(amount, currencyCode, options);
  }

  /**
   * Format currency for display in forms (without symbol, for input fields)
   */
  formatCurrencyForInput(amount: number, currencyCode?: string): string {
    return this.formatCurrency(amount, currencyCode, {
      showSymbol: false,
      showCode: false,
    });
  }

  /**
   * Format currency with GST breakdown (Indian tax system)
   */
  formatCurrencyWithGST(
    baseAmount: number,
    gstRate: number = 0.18,
    currencyCode?: string
  ): {
    base: string;
    gst: string;
    total: string;
  } {
    const gstAmount = baseAmount * gstRate;
    const totalAmount = baseAmount + gstAmount;

    return {
      base: this.formatCurrency(baseAmount, currencyCode),
      gst: this.formatCurrency(gstAmount, currencyCode),
      total: this.formatCurrency(totalAmount, currencyCode),
    };
  }

  /**
   * Format currency with GST breakdown from minor units
   */
  formatCurrencyWithGSTFromMinorUnit(
    baseAmountInMinorUnit: number,
    gstRate: number = 0.18,
    currencyCode?: string
  ): {
    base: string;
    gst: string;
    total: string;
  } {
    const config = this.getCurrencyConfig(currencyCode);
    const divisor = Math.pow(10, config.decimalPlaces);
    const baseAmount = baseAmountInMinorUnit / divisor;
    
    return this.formatCurrencyWithGST(baseAmount, gstRate, currencyCode);
  }

  /**
   * Parse currency string to number (remove symbols and formatting)
   */
  parseCurrency(currencyString: string, currencyCode?: string): number {
    const config = this.getCurrencyConfig(currencyCode);
    
    // Remove currency symbol and code
    let cleanString = currencyString
      .replace(config.symbol, '')
      .replace(config.code, '')
      .trim();

    // Remove locale-specific thousand separators and convert decimal separator
    if (config.locale === 'en-IN') {
      // Indian number format: 1,23,456.78
      cleanString = cleanString.replace(/,/g, '');
    } else if (config.locale === 'en-US') {
      // US number format: 123,456.78
      cleanString = cleanString.replace(/,/g, '');
    } else if (config.locale === 'en-EU') {
      // European format: 123.456,78
      cleanString = cleanString.replace(/\./g, '').replace(',', '.');
    }

    const parsed = parseFloat(cleanString);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Convert currency amount to minor units (paise/cents)
   */
  toMinorUnit(amount: number, currencyCode?: string): number {
    const config = this.getCurrencyConfig(currencyCode);
    const multiplier = Math.pow(10, config.decimalPlaces);
    return Math.round(amount * multiplier);
  }

  /**
   * Convert from minor units to main currency unit
   */
  fromMinorUnit(amountInMinorUnit: number, currencyCode?: string): number {
    const config = this.getCurrencyConfig(currencyCode);
    const divisor = Math.pow(10, config.decimalPlaces);
    return amountInMinorUnit / divisor;
  }

  /**
   * Validate currency amount
   */
  validateAmount(
    amount: number,
    options?: {
      minAmount?: number;
      maxAmount?: number;
      allowZero?: boolean;
      allowNegative?: boolean;
    }
  ): { isValid: boolean; error?: string } {
    const {
      minAmount = 0,
      maxAmount = Number.MAX_SAFE_INTEGER,
      allowZero = false,
      allowNegative = false,
    } = options || {};

    if (isNaN(amount) || !isFinite(amount)) {
      return { isValid: false, error: 'Please enter a valid amount' };
    }

    if (!allowNegative && amount < 0) {
      return { isValid: false, error: 'Amount cannot be negative' };
    }

    if (!allowZero && amount === 0) {
      return { isValid: false, error: 'Amount must be greater than zero' };
    }

    if (amount < minAmount) {
      return { isValid: false, error: `Amount must be at least ${this.formatCurrency(minAmount)}` };
    }

    if (amount > maxAmount) {
      return { isValid: false, error: `Amount cannot exceed ${this.formatCurrency(maxAmount)}` };
    }

    return { isValid: true };
  }

  /**
   * Format currency range (e.g., "₹100 - ₹500")
   */
  formatCurrencyRange(
    minAmount: number,
    maxAmount: number,
    currencyCode?: string,
    separator: string = ' - '
  ): string {
    const min = this.formatCurrency(minAmount, currencyCode);
    const max = this.formatCurrency(maxAmount, currencyCode);
    return `${min}${separator}${max}`;
  }

  /**
   * Get currency symbol only
   */
  getCurrencySymbol(currencyCode?: string): string {
    const config = this.getCurrencyConfig(currencyCode);
    return config.symbol;
  }

  /**
   * Check if a currency is supported
   */
  isCurrencySupported(currencyCode: string): boolean {
    return currencyCode in this.supportedCurrencies;
  }

  /**
   * Add or update currency configuration
   */
  addCurrency(currencyCode: string, config: CurrencyConfig): void {
    this.supportedCurrencies[currencyCode] = config;
  }
}

// Export singleton instance
export const currencyFormattingService = new CurrencyFormattingService();

// Export class for testing
export { CurrencyFormattingService };